import { For, Match, onCleanup, Switch, type JSX } from "solid-js"
import { drillScope } from "./drill-column-core"
import { EditorSkeleton, editorView } from "./editor-skeleton"
import { ReportsPageSkeleton } from "./reports-skeleton"
import { Bar, skeletonTiming } from "./skeleton"
import { SourceFilesSkeleton } from "./source-files-skeleton"
import { WorkflowsSkeleton } from "./workflows-skeleton"
import "./tokens.css"

// The report editor (/reports/:projectID/:workspaceID): a full-window workspace with its own rail (the shell drops its own
// there), so its skeleton is the whole window (§64).
const EDITOR = /^\/reports\/[^/]+\/[^/]+\/?$/

// The V2 page a URL opens, while it loads (DESIGN-LOG §63): the content area's skeleton for the rail's four places,
// `fallback` for any other page. The shell shows it while a page's code loads on a fresh visit, and silent sign-in
// shows it under its rail, so a hard load reads as one skeleton from the first frame to the data. `label`: what Reports'
// skeleton tells screen readers (sign-in says it is signing in). Workflows and Source files draw their column at the
// URL's level (drillScope; the classic run route is under All clients).
export function PageSkeleton(props: { path: string; search: string; label?: string; fallback?: JSX.Element }) {
  const query = () => new URLSearchParams(props.search)
  return (
    <Switch fallback={props.fallback}>
      <Match when={/^\/reports\/?$/.test(props.path)}><ReportsPageSkeleton label={props.label} /></Match>
      <Match when={EDITOR.test(props.path)}><div class="dx-v2 dxe dxe-sk-page"><EditorSkeleton view={editorView(query().get("view"))} label={props.label} /></div></Match>
      <Match when={/^\/catalog\/workflows\/?$/.test(props.path)}><WorkflowsSkeleton update={!!query().get("run")} drill={drillScope(Object.fromEntries(query()))} /></Match>
      <Match when={/^\/catalog\/workflows\/[^/]+\/[^/]+\/runs(\/[^/]+)?\/?$/.exec(props.path)}>{(match) => <WorkflowsSkeleton update={!!match()[1]} drill="all" />}</Match>
      <Match when={/^\/files\/?$/.test(props.path)}><SourceFilesSkeleton all={query().get("view") === "all"} drill={drillScope(Object.fromEntries(query()))} /></Match>
    </Switch>
  )
}

// Silent sign-in (P21): the app's outline (the rail, and the page the URL opens; Reports for any other) instead of a
// full-screen "Preparing sign in…", and the tab already says Lesha Bank. The status text stays for screen readers. The
// sidebar's width follows what the shell will show: expanded on a wide window unless it was collapsed.
export function LeshaBootSkeleton(props: { label: string; title: string }) {
  const previous = document.title
  document.title = props.title
  onCleanup(() => {
    if (document.title === props.title) document.title = previous
  })
  const collapsed = () => {
    try {
      return Object.keys(localStorage).some((key) => key.startsWith("dx-v2-sidebar:") && localStorage.getItem(key) === "collapsed")
    } catch {
      return false
    }
  }
  const expanded = (typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 1100px)").matches) && !collapsed()
  // The editor has no shell rail: its own frame fills the window (§64).
  if (EDITOR.test(location.pathname)) return (
    <div class="dx-v2-shell dx-v2 dx-skel dx-skel-boot" style={skeletonTiming()}>
      <PageSkeleton path={location.pathname} search={location.search} label={props.label} />
    </div>
  )
  return (
    <div class="dx-v2-shell dx-v2 dx-skel dx-skel-boot" style={skeletonTiming()}>
      <nav class="dx-v2-rail" data-expanded={expanded || undefined} aria-hidden="true">
        {/* The rail's head (DESIGN-LOG §57): the mark on its tile, and in the sidebar a bar where the company goes. */}
        <span class="dx-skel-head">
          <span class="dx-v2-rail-tile">
            <img src="/lesha-bank-mark.png" alt="" />
          </span>
          {expanded && <Bar line={false} />}
        </span>
        <For each={Array.from({ length: 4 })}>{() => <span class="dx-skel-row"><Bar line={false} /></span>}</For>
      </nav>
      <PageSkeleton path={location.pathname} search={location.search} label={props.label} fallback={<ReportsPageSkeleton label={props.label} />} />
    </div>
  )
}
